import React from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import H2 from 'apps/Pdf/components/common/H2';
import PageBreak from 'apps/Pdf/components/common/PageBreak';
import useGeneralStyles from 'apps/Pdf/styles/general';
import useStyles from './QuestionsAndAnswersStyle';
import { structure } from 'pages/Accounts/Edit/Investments/questionnaireStructure';
import { QAA } from '../InvestmentQuestionnaireInterface';
import { Currency } from 'enums';

interface Props {
  data: QAA;
  reasons: { [key: string]: { previous: string | string[]; reason: string } };
  version: string;
  currency: Currency;
}

const QuestionsAndAnswers = (props: Props) => {
  const styles = useGeneralStyles();
  const classes = useStyles();
  const { t } = useTranslation();

  const getLabelFromValue = (name: string, value: string | string[]) => {
    return value === '' ? '…' : t(`accountInvestment_v${props.version}_${name}_${value}`);
  };

  return (
    <>
      {Object.keys(structure[props.version]).map((tabKey, tabIndex) => {
        const tab = structure[props.version][tabKey as unknown as number];
        return (
          <React.Fragment key={tabKey}>
            {tabIndex > 0 && <PageBreak />}
            <H2>{t(`accountInvestment_v${props.version}_panel${tabKey}Heading`)}</H2>
            {tab.map((q) => {
              return q.name in props.data ? (
                <React.Fragment key={q.name}>
                  <Grid container className={classes.qa}>
                    {q.images ? (
                      <>
                        <Grid item xs={12}>
                          {t(
                            `accountInvestment_v${props.version}_${q.name}label`,
                            q.valueInTitle
                              ? { value: getLabelFromValue(q.valueInTitle, props.data[q.valueInTitle]) }
                              : undefined,
                          )}
                        </Grid>
                        <Grid item container xs={12}>
                          {q.images.map((img, i) => {
                            const cssClass =
                              (Array.isArray(props.data[q.name]) &&
                                props.data[q.name].indexOf(q.order ? q.order[i] : (i + 1).toString()) !== -1) ||
                              (props.data[q.name] as string) === (q.order ? q.order[i] : (i + 1).toString())
                                ? `${classes.selectedImg}`
                                : undefined;

                            return (
                              <Grid item xs={4} key={`${q.name}_image_${i + 1}`}>
                                <img width="200" alt="" src={img} className={cssClass} />
                              </Grid>
                            );
                          })}
                        </Grid>
                        {props.reasons[q.name]?.reason && (
                          <Grid item container xs={12}>
                            <strong>
                              {t('accountInvestment_prevValue')}:&nbsp;
                              {props.reasons[q.name].previous}
                              <br />
                              {t('accountInvestment_reason')}:&nbsp;
                              {props.reasons[q.name].reason}
                            </strong>
                          </Grid>
                        )}
                      </>
                    ) : (
                      <>
                        <Grid item xs={6}>
                          {t(
                            `accountInvestment_v${props.version}_${q.name}label`,
                            q.valueInTitle
                              ? { value: getLabelFromValue(q.valueInTitle, props.data[q.valueInTitle]) }
                              : undefined,
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          <ul>
                            {new Array(q.options).fill(0).map((_value, i) => {
                              const cssClass =
                                (Array.isArray(props.data[q.name]) &&
                                  props.data[q.name].indexOf(q.order ? q.order[i] : (i + 1).toString()) !== -1) ||
                                (props.data[q.name] as string) === (q.order ? q.order[i] : (i + 1).toString())
                                  ? `${styles.bold} ${styles.value}`
                                  : undefined;
                              return (
                                <li
                                  className={cssClass}
                                  key={`${q.name}_${i + 1}`}
                                  dangerouslySetInnerHTML={{
                                    __html: t(
                                      `accountInvestment_v${props.version}_${q.name}_${q.order ? q.order[i] : i + 1}${
                                        q.currency ? '_' + props.currency : ''
                                      }`,
                                      {
                                        interpolation: { escapeValue: false },
                                      },
                                    ),
                                  }}
                                />
                              );
                            })}
                          </ul>
                        </Grid>
                        {props.reasons[q.name]?.reason && (
                          <Grid item container xs={12}>
                            <strong>
                              {t('accountInvestment_prevValue')}:&nbsp;
                              {Array.isArray(props.reasons[q.name].previous)
                                ? (props.reasons[q.name].previous as string[])
                                    .map((a) =>
                                      t(
                                        `accountInvestment_v${props.version}_${q.name}_${a}${
                                          q.currency ? '_' + props.currency : ''
                                        }`,
                                      ),
                                    )
                                    .join(', ')
                                : t(
                                    `accountInvestment_v${props.version}_${q.name}_${props.reasons[q.name].previous}${
                                      q.currency ? '_' + props.currency : ''
                                    }`,
                                  )}
                              <br />
                              {t('accountInvestment_reason')}:&nbsp;
                              {props.reasons[q.name].reason}
                            </strong>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </React.Fragment>
              ) : null;
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default QuestionsAndAnswers;
